"use strict";
/*
 "libmime" customised for Vereign SEAL
 Copyright (C) 2020-2021, Vereign AG
 This program is free software: you can redistribute it and/or modify
 it under the terms of the GNU Affero General Public License as
 published by the Free Software Foundation, either version 3 of the
 License, or (at your option) any later version.
 This program is distributed in the hope that it will be useful,
 but WITHOUT ANY WARRANTY; without even the implied warranty of
 MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details.
 You should have received a copy of the GNU Affero General Public License
 along with this program.  If not, see https://www.gnu.org/licenses/.
 In case of queries or questions, please contact us under by email under contact@vereign.com or by letter post to  Vereign AG, Kolinplatz 10, 6300 Zug, Switzerland


 This program ("libmime" customised for Vereign SEAL) incorporates work covered by the following copyright and permission notice:
 "libmime"
 Copyright (c) 2014-2016 Andris Reinman
 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated documentation files (the "Software"), to deal
 in the Software without restriction, including without limitation the rights
 to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the Software is
 furnished to do so, subject to the following conditions:
 The above copyright notice and this permission notice shall be included in
 all copies or substantial portions of the Software.
 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 THE SOFTWARE
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.decodeHeaders = exports.decodeWords = exports.parseHeaderValue = void 0;
const charset_1 = require("./charset");
const STAGE_KEY = 0x1001;
const STAGE_VALUE = 0x1002;
/**
 * Parses a header value with key=value arguments into a structured
 * object.
 *
 *   parseHeaderValue('content-type: text/plain; CHARSET='UTF-8'') ->
 *   {
 *     'value': 'text/plain',
 *     'params': {
 *       'charset': 'UTF-8'
 *     }
 *   }
 *
 * @param {String} str Header value
 * @return {Object} Header value as a parsed structure
 */
const parseHeaderValue = (str) => {
    let response = {
        value: false,
        params: {},
    };
    let key = false;
    let value = "";
    let stage = STAGE_VALUE;
    let quote = false;
    let escaped = false;
    let chr;
    for (let i = 0, len = str.length; i < len; i++) {
        chr = str.charAt(i);
        switch (stage) {
            case STAGE_KEY:
                if (chr === "=") {
                    key = value.trim().toLowerCase();
                    stage = STAGE_VALUE;
                    value = "";
                    break;
                }
                value += chr;
                break;
            case STAGE_VALUE:
                if (escaped) {
                    value += chr;
                }
                else if (chr === "\\") {
                    escaped = true;
                    continue;
                }
                else if (quote && chr === quote) {
                    quote = false;
                }
                else if (!quote && chr === '"') {
                    quote = chr;
                }
                else if (!quote && chr === ";") {
                    if (key === false) {
                        response.value = value.trim();
                    }
                    else {
                        response.params[key] = value.trim();
                    }
                    stage = STAGE_KEY;
                    value = "";
                }
                else {
                    value += chr;
                }
                escaped = false;
                break;
        }
    }
    // finalize remainder
    value = value.trim();
    if (stage === STAGE_VALUE) {
        if (key === false) {
            // default value
            response.value = value;
        }
        else {
            // subkey value
            response.params[key] = value;
        }
    }
    else if (value) {
        // treat as key without value, see emptykey:
        // Header-Key: somevalue; key=value; emptykey
        response.params[value.toLowerCase()] = "";
    }
    // handle parameter value continuations
    // https://tools.ietf.org/html/rfc2231#section-3
    // preprocess values
    Object.keys(response.params).forEach((key) => {
        let actualKey;
        let nr;
        let value;
        let match = key.match(/\*((\d+)\*?)?$/);
        if (!match) {
            // nothing to do here, does not seem like a continuation param
            return;
        }
        actualKey = key.substr(0, match.index).toLowerCase();
        nr = Number(match[2]) || 0;
        if (!response.params[actualKey] ||
            typeof response.params[actualKey] !== "object") {
            response.params[actualKey] = {
                charset: false,
                values: [],
            };
        }
        value = response.params[key];
        if (nr === 0 &&
            match[0].charAt(match[0].length - 1) === "*" &&
            (match = value.match(/^([^']*)'[^']*'(.*)$/))) {
            response.params[actualKey].charset = match[1] || "utf-8";
            value = match[2];
        }
        response.params[actualKey].values.push({ nr, value });
        // remove the old reference
        delete response.params[key];
    });
    // concatenate split rfc2231 strings and convert encoded strings to mime encoded words
    Object.keys(response.params).forEach((key) => {
        let value;
        if (response.params[key] && Array.isArray(response.params[key].values)) {
            value = response.params[key].values
                .sort((a, b) => a.nr - b.nr)
                .map((val) => (val && val.value) || "")
                .join("");
            if (response.params[key].charset) {
                // convert "%AB" to "=?charset?Q?=AB?=" and then to unicode
                response.params[key] = exports.decodeWords("=?" +
                    response.params[key].charset +
                    "?Q?" +
                    value
                        // fix invalidly encoded chars
                        .replace(/[=?_\s]/g, (s) => {
                        let c = s.charCodeAt(0).toString(16);
                        if (s === " ") {
                            return "_";
                        }
                        else {
                            return "%" + (c.length < 2 ? "0" : "") + c;
                        }
                    })
                        // change from urlencoding to percent encoding
                        .replace(/%/g, "=") +
                    "?=");
            }
            else {
                response.params[key] = exports.decodeWords(value);
            }
        }
    });
    return response;
};
exports.parseHeaderValue = parseHeaderValue;
const getHex = (c) => {
    if ((c >= 0x30 /* 0 */ && c <= 0x39) /* 9 */ ||
        (c >= 0x61 /* a */ && c <= 0x66) /* f */ ||
        (c >= 0x41 /* A */ && c <= 0x46) /* F */) {
        return String.fromCharCode(c);
    }
    return false;
};
/**
 * Decode a complete mime word encoded string
 *
 * @param {String} str Mime word encoded string
 * @return {String} Decoded unicode string
 */
const decodeWord = (charset, encoding, str) => {
    // RFC2231 added language tag to the encoding
    // see: https://tools.ietf.org/html/rfc2231#section-5
    // this implementation silently ignores this tag
    let splitPos = charset.indexOf("*");
    if (splitPos >= 0) {
        charset = charset.substr(0, splitPos);
    }
    charset = charset_1.normalizeCharset(charset);
    encoding = encoding.toUpperCase();
    let bufToDecode;
    if (encoding === "Q") {
        str = str
            // remove spaces between = and hex char, this might indicate invalidly applied line splitting
            .replace(/=\s+([0-9a-fA-F])/g, "=$1")
            // convert all underscores to spaces
            .replace(/[_\s]/g, " ");
        let buf = Buffer.from(str);
        let bytes = [];
        for (let i = 0, len = buf.length; i < len; i++) {
            let c = buf[i];
            if (i <= len - 2 && c === 0x3d /* = */) {
                let c1 = getHex(buf[i + 1]);
                let c2 = getHex(buf[i + 2]);
                if (c1 && c2) {
                    let c = parseInt(c1 + c2, 16);
                    bytes.push(c);
                    i += 2;
                    continue;
                }
            }
            bytes.push(c);
        }
        bufToDecode = Buffer.from(bytes);
    }
    else if (encoding === "B") {
        bufToDecode = Buffer.concat(str
            .split("=")
            .filter((s) => s !== "") // filter empty string
            .map((str) => Buffer.from(str, "base64")));
    }
    else {
        // keep as is, convert Buffer to unicode string, assume utf8
        bufToDecode = Buffer.from(str);
    }
    return charset_1.decodeCharset(bufToDecode, charset);
};
/**
 * Decode a string that might include one or several mime words
 *
 * @param {String} str String including some mime words that will be encoded
 * @return {String} Decoded unicode string
 */
const decodeWords = (str) => {
    return ((str || "")
        .toString()
        // find base64 words that can be joined
        .replace(/(=\?([^?]+)\?[Bb]\?[^?]*\?=)\s*(?==\?([^?]+)\?[Bb]\?[^?]*\?=)/g, (match, left, chLeft, chRight) => {
        // only mark b64 chunks to be joined if charsets match
        if (charset_1.normalizeCharset(chLeft || "") === charset_1.normalizeCharset(chRight || "")) {
            // set a joiner marker
            return left + "__\x00JOIN\x00__";
        }
        return match;
    })
        // find QP words that can be joined
        .replace(/(=\?([^?]+)\?[Qq]\?[^?]*\?=)\s*(?==\?([^?]+)\?[Qq]\?[^?]*\?=)/g, (match, left, chLeft, chRight) => {
        // only mark QP chunks to be joined if charsets match
        if (charset_1.normalizeCharset(chLeft || "") === charset_1.normalizeCharset(chRight || "")) {
            // set a joiner marker
            return left + "__\x00JOIN\x00__";
        }
        return match;
    })
        // join base64 encoded words
        .replace(/(\?=)?__\x00JOIN\x00__(=\?([^?]+)\?[QqBb]\?)?/g, "")
        // remove spaces between mime encoded words
        .replace(/(=\?[^?]+\?[QqBb]\?[^?]*\?=)\s+(?==\?[^?]+\?[QqBb]\?[^?]*\?=)/g, "$1")
        // decode words
        .replace(/=\?([\w_\-*]+)\?([QqBb])\?([^?]*)\?=/g, (m, charset, encoding, text) => decodeWord(charset, encoding, text)));
};
exports.decodeWords = decodeWords;
/**
 * Parses a block of header lines. Does not decode mime words as every
 * header might have its own rules (eg. formatted email addresses and such)
 *
 * @param {String} headers Headers string
 * @return {Object} An object of headers, where header keys are object keys. NB! Several values with the same key make up an Array
 */
const decodeHeaders = (headers) => {
    let lines = headers.split(/\r?\n|\r/), headersObj = {}, header, i, len;
    for (i = lines.length - 1; i >= 0; i--) {
        if (i && lines[i].match(/^\s/)) {
            lines[i - 1] += "\r\n" + lines[i];
            lines.splice(i, 1);
        }
    }
    for (i = 0, len = lines.length; i < len; i++) {
        header = decodeHeader(lines[i]);
        if (!headersObj[header.key]) {
            headersObj[header.key] = [header.value];
        }
        else {
            headersObj[header.key].push(header.value);
        }
    }
    return headersObj;
};
exports.decodeHeaders = decodeHeaders;
/**
 * Splits a string by :
 * The result is not mime word decoded, you need to do your own decoding based
 * on the rules for the specific header key
 *
 * @param {String} headerLine Single header line, might include linebreaks as well if folded
 * @return {Object} And object of {key, value}
 */
const decodeHeader = (headerLine) => {
    let line = (headerLine || "")
        .toString()
        .replace(/(?:\r?\n|\r)[ \t]*/g, " ")
        .trim(), match = line.match(/^\s*([^:]+):(.*)$/), key = ((match && match[1]) || "").trim().toLowerCase(), value = ((match && match[2]) || "").trim();
    return {
        key,
        value,
    };
};
